import React, { useState, useEffect, useRef } from 'react'
import {
  AppDialog,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogDescription,
  AppDialogBody,
  useToastV2,
} from '@easyllama/ui-components'
import { useApp } from '~/context/appContext'
import BillingPlansCards from '~/molecules/Billing/BillingPlansCards'
import BillingFAQ from '~/molecules/Billing/BillingFAQ'
import ContactSalesButton from '~/molecules/Billing/ContactSalesButton'
import ContactSalesDialog from '~/molecules/Billing/ContactSalesDialog'
import { fetchPlans } from '~/service/Billing/fetchApiClient'
import ChangePlanDialog from '~/organisms/Billing/ChangePlanDialog'
import { UPGRADE_SOURCE_COURSE_AUTHORING } from '~/utils/upgradeUrl'

const DEFAULT_COPY = {
  title: 'Pick the plan that best protects your business',
  description:
    "EasyLlama's plans are built for your goals, number of employees, and current needs, whether you're looking for one-off compliance courses or unlimited access to our full course library and advanced AI-powered course creation and customization.",
}

const COPY_BY_SOURCE = {
  [UPGRADE_SOURCE_COURSE_AUTHORING]: {
    title: 'Unlock AI Course Authoring with Ultimate',
    description:
      'Turn your existing documents and expertise into engaging training using the AI Course Authoring Tool. Create AI-generated courses, upload SCORM content, and duplicate customized courses to manage your complete compliance and training program in one place.',
  },
}

export default function PlansDialog({ open, onOpenChange, initialCount, initialUnit, restrictUnit = null, source = null, onSuccess = null, onViewDetails = null }) {
  const copy = COPY_BY_SOURCE[source] ?? DEFAULT_COPY
  const { company, jwt, subscription } = useApp()
  const toast = useToastV2()
  const [comparisonOpen, setComparisonOpen] = useState(false)
  const [contactSalesOpen, setContactSalesOpen] = useState(false)
  const [plans, setPlans] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [changePlanOpen, setChangePlanOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [selectedCount, setSelectedCount] = useState(null)
  const [estimate, setEstimate] = useState(null)
  const [selectingPlanId, setSelectingPlanId] = useState(null)
  const activePlanUnitRef = useRef(subscription?.planLicense?.unit)
  const lastFetchedQuantityRef = useRef(null)
  const prevOpenRef = useRef(false)
  const fetchFailedRef = useRef(false)

  useEffect(() => {
    const currentUnit = subscription?.planLicense?.unit
    if (activePlanUnitRef.current !== currentUnit) {
      activePlanUnitRef.current = currentUnit
      setPlans([])
      lastFetchedQuantityRef.current = null
    }
  }, [subscription])

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false
      return
    }
    // On each false→true open transition: if the last fetch failed, reset so it retries.
    // Successful fetches stay cached — prevOpenRef prevents resetting on every dep change.
    if (!prevOpenRef.current) {
      prevOpenRef.current = true
      if (fetchFailedRef.current) {
        fetchFailedRef.current = false
        lastFetchedQuantityRef.current = null
      }
    }
    const isNewOrCanceled = !subscription || subscription.status === 'canceled'
    const plansQuantity = initialCount != null
      ? (isNewOrCanceled ? initialCount : (subscription.licensesCount || 0) + initialCount)
      : (isNewOrCanceled ? undefined : (subscription.licensesCount || 0))
    if (lastFetchedQuantityRef.current === plansQuantity) return
    lastFetchedQuantityRef.current = plansQuantity
    setIsLoading(true)
    fetchPlans(company.id, jwt, plansQuantity)
      .then(setPlans)
      .catch(() => {
        setPlans([])
        fetchFailedRef.current = true
      })
      .finally(() => setIsLoading(false))
  }, [open, subscription?.status, subscription?.licensesCount, initialCount])

  const handleOpenChange = (value) => {
    if (!value) {
      setComparisonOpen(false)
      setContactSalesOpen(false)
    }
    onOpenChange(value)
  }

  const handleSelect = (plan, count) => {
    const planData = plans.find((p) => p.unit === plan.id)
    setSelectedPlan(plan)
    setSelectedCount(count)
    setEstimate(planData?.estimate ?? null)
    handleOpenChange(false)
    setChangePlanOpen(true)
  }

  return (
    <>
      <AppDialog open={open} onOpenChange={handleOpenChange}>
        <AppDialogContent size='xl' className='!pb-0' data-safari-pb-0 onInteractOutside={(e) => {
          const target = e.detail?.originalEvent?.target
          if (target instanceof Element && target.closest('[data-radix-portal]')) return
          e.preventDefault()
        }}>

          <AppDialogHeader>
            <AppDialogTitle className='el:sr-only'>
              {copy.title}
            </AppDialogTitle>
            <AppDialogDescription className='el:sr-only'>
              Select a billing plan
            </AppDialogDescription>
          </AppDialogHeader>

          <AppDialogBody className='el:border-t-0 el:p-0 rounded-bl-[20px] rounded-br-[20px]' data-safari-p-0>

            {/* Title + description */}
            <div className='text-center px-10 pt-2 text-llama-grey-7'>
              <h1 className='text-[36px] leading-[1] mb-4 text-llama-grey-7 font-[RecoletaSemiBold]'>
                {copy.title}
              </h1>
              <p className='text-lg font-semibold max-w-dialog-description mx-auto leading-[1.7] text-llama-grey-7'>
                {copy.description}
              </p>
            </div>

            {/* 'Questions?' bar — full-width separator row */}
            <div className='flex items-center justify-center gap-3 mt-10 mb-0 -mx-6 px-6'>
              <span className='text-lg font-bold text-llama-grey-7'>
                Questions about the plan?
              </span>
              <ContactSalesButton className='text-sm' onClick={() => setContactSalesOpen(true)} />
            </div>

            {/* Plan cards */}
            <BillingPlansCards
              plans={plans}
              onSelect={handleSelect}
              comparisonOpen={comparisonOpen}
              onComparisonOpen={() => setComparisonOpen(true)}
              isLoading={isLoading}
              selectingPlanId={selectingPlanId}
              initialCount={initialCount}
              initialUnit={initialUnit}
              restrictUnit={restrictUnit}
            />

            {/* Need Help + FAQ — visible after 'See feature comparison' is opened */}
            {comparisonOpen && (
              <>
                {/* data-safari-* markers restore this spacing in Safari; see
                    _in_app_announcement_styling.html.erb. */}
                <div className='text-center py-12 px-6' data-safari-py-12 data-safari-px-6 data-safari-text-center>
                  <h3 className='text-2xl mb-5 text-llama-grey-7 font-[RecoletaSemiBold]' data-safari-mb-5>
                    Need Help Finding The Right Plan?
                  </h3>
                  <ContactSalesButton size='lg' onClick={() => setContactSalesOpen(true)} />
                </div>

                <BillingFAQ />
              </>
            )}
          </AppDialogBody>
        </AppDialogContent>

        <ContactSalesDialog open={contactSalesOpen} handleOpenChange={setContactSalesOpen} />
      </AppDialog>

      <ChangePlanDialog
        open={changePlanOpen}
        onOpenChange={setChangePlanOpen}
        plans={plans}
        initialPlan={selectedPlan}
        initialCount={selectedCount}
        initialEstimate={estimate}
        source={source}
        restrictUnit={restrictUnit}
        onSuccess={onSuccess}
        onViewDetails={onViewDetails}
      />
    </>
  )
}
